import { useEffect, useState } from 'react';
import { ShieldCheck, Loader2, Laptop, Ban, RotateCcw, Copy, Check, Download, AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';
import { useDialog } from '../../components/DialogProvider';
import { useAuth } from '../../lib/auth';
import { Modal } from '../../components/Modal';
import { StatusBadge } from '../../components/StatusBadge';
import { MenuAcoesLinha } from '../../components/MenuAcoesLinha';

interface Dispositivo {
  id: number;
  nome: string;
  ultimo_acesso: string | null;
  ativo: boolean;
  criado_em: string;
}

interface CertificadoEmitido {
  pfx_base64: string;
  senha: string;
  fingerprint: string;
  ca_issuer_cn: string;
  nome: string;
}

// O backend ja entrega a data formatada em BRT (to_char) — so exibir, sem reparsear (parsear
// como Date reinterpretaria a hora naive como UTC e mostraria 3h a menos).
function formatarData(valor: string | null): string {
  return valor || '—';
}

function nomeArquivo(nome: string): string {
  // Acentos viram separador (sem normalize p/ evitar fragilidade de encoding no source).
  const base = nome.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `fiodecobre-${base || 'dispositivo'}.pfx`;
}

function dispararDownload(blob: Blob, nomeArq: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArq;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function baixarPfx(base64: string, nome: string): void {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  dispararDownload(new Blob([bytes], { type: 'application/x-pkcs12' }), nomeArquivo(nome));
}

// Arquivo .reg que ensina o navegador a escolher SOZINHO o certificado da loja para este site,
// em vez de perguntar "qual certificado?" a cada acesso. Filtra pelo emissor (nossa CA) — o
// mesmo .reg vale para qualquer maquina, pois o emissor e o mesmo para todas. Escreve a
// politica AutoSelectCertificateForUrls em HKLM (maquina toda; pede o "Sim" de administrador).
// Cobre Chrome E Edge (ambos Chromium, mesma politica, caminhos diferentes no registro) — a loja
// tem maquinas nos dois. Gerado em UTF-16 LE com BOM, formato que o Editor do Registro aceita.
function baixarLiberacaoNavegador(issuerCn: string): void {
  const entrada = JSON.stringify({ pattern: window.location.origin, filter: { ISSUER: { CN: issuerCn } } });
  const linhaValor = '"1"="' + entrada.replace(/"/g, '\\"') + '"';
  const texto =
    'Windows Registry Editor Version 5.00\r\n\r\n' +
    '[HKEY_LOCAL_MACHINE\\SOFTWARE\\Policies\\Google\\Chrome\\AutoSelectCertificateForUrls]\r\n' +
    linhaValor + '\r\n\r\n' +
    '[HKEY_LOCAL_MACHINE\\SOFTWARE\\Policies\\Microsoft\\Edge\\AutoSelectCertificateForUrls]\r\n' +
    linhaValor + '\r\n';
  const bytes = new Uint8Array(texto.length * 2 + 2);
  bytes[0] = 0xff; // BOM UTF-16 LE
  bytes[1] = 0xfe;
  for (let i = 0; i < texto.length; i++) {
    const c = texto.charCodeAt(i);
    bytes[2 + i * 2] = c & 0xff;
    bytes[2 + i * 2 + 1] = c >> 8;
  }
  dispararDownload(new Blob([bytes], { type: 'application/octet-stream' }), 'fiodecobre-liberar-navegador.reg');
}

export function DispositivosAutorizados() {
  const dialog = useDialog();
  const { usuario } = useAuth();
  const ehAdmin = usuario?.perfil === 'admin';

  const [ativo, setAtivo] = useState(false);
  const [lista, setLista] = useState<Dispositivo[]>([]);
  const [esteComputador, setEsteComputador] = useState<{ autorizado: boolean; nome: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [salvandoFlag, setSalvandoFlag] = useState(false);

  // Modal "Autorizar este computador"
  const [modalAberto, setModalAberto] = useState(false);
  const [apelido, setApelido] = useState('');
  const [emitindo, setEmitindo] = useState(false);
  const [emitido, setEmitido] = useState<CertificadoEmitido | null>(null);
  const [copiado, setCopiado] = useState(false);

  async function carregar() {
    setLoading(true);
    try {
      const [flag, devs, eu] = await Promise.all([
        api.get<{ valor: string | null }>('/api/app-settings/device_trust_ativo').catch(() => ({ valor: null })),
        api.get<Dispositivo[]>('/api/dispositivos').catch(() => [] as Dispositivo[]),
        api.get<{ autorizado: boolean; nome: string | null }>('/api/dispositivos/este-computador').catch(() => ({ autorizado: false, nome: null })),
      ]);
      setAtivo(flag?.valor === 'true');
      setLista(devs);
      setEsteComputador(eu);
    } catch (e: any) {
      dialog.toast(e.message ?? 'Falha ao carregar', { tipo: 'erro' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void carregar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function alternarFlag(ligar: boolean) {
    if (ligar) {
      const ok = await dialog.confirmar(
        'Ao ligar, TODA máquina que não estiver cadastrada aqui deixa de acessar o sistema (exceto os logins de emergência do Ruan). '
        + 'Confirme que este computador — e os demais que você usa — já estão autorizados na lista abaixo antes de ligar.',
        { titulo: 'Exigir dispositivo autorizado', perigoso: true, textoConfirmar: 'Ligar mesmo assim' },
      );
      if (!ok) return;
    }
    setSalvandoFlag(true);
    try {
      await api.put('/api/app-settings/device_trust_ativo', { valor: ligar ? 'true' : 'false' });
      setAtivo(ligar);
      dialog.toast(ligar ? 'Trava de dispositivo LIGADA' : 'Trava de dispositivo desligada', { tipo: 'sucesso' });
    } catch (e: any) {
      dialog.toast(e.message ?? 'Falha ao salvar', { tipo: 'erro' });
    } finally {
      setSalvandoFlag(false);
    }
  }

  function abrirModal() {
    setApelido('');
    setEmitido(null);
    setCopiado(false);
    setModalAberto(true);
  }

  async function autorizar() {
    const nome = apelido.trim();
    if (!nome) { dialog.toast('Dê um apelido ao computador (ex: Balcão 1)', { tipo: 'aviso' }); return; }
    setEmitindo(true);
    try {
      const r = await api.post<CertificadoEmitido>('/api/dispositivos/autorizar', { nome });
      // So o .pfx baixa automatico. O .reg fica num botao explicito no Passo 2: o Chrome
      // BLOQUEIA o segundo download automatico da mesma acao ("baixar varios arquivos"),
      // entao o .reg sumia silenciosamente. Clique do usuario nunca e bloqueado.
      baixarPfx(r.pfx_base64, r.nome);
      setEmitido(r);
      await carregar();
    } catch (e: any) {
      dialog.toast(e.message ?? 'Falha ao autorizar', { tipo: 'erro' });
    } finally {
      setEmitindo(false);
    }
  }

  async function copiarSenha() {
    if (!emitido) return;
    try {
      await navigator.clipboard.writeText(emitido.senha);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      dialog.toast('Não consegui copiar — selecione e copie manualmente', { tipo: 'aviso' });
    }
  }

  async function revogar(d: Dispositivo) {
    const ok = await dialog.confirmar(
      `Revogar "${d.nome}"? Este computador deixa de acessar o sistema (enquanto a trava estiver ligada).`,
      { titulo: 'Revogar dispositivo', perigoso: true, textoConfirmar: 'Revogar' },
    );
    if (!ok) return;
    try {
      await api.post(`/api/dispositivos/${d.id}/revogar`);
      await carregar();
      dialog.toast('Dispositivo revogado', { tipo: 'sucesso' });
    } catch (e: any) {
      dialog.toast(e.message ?? 'Falha ao revogar', { tipo: 'erro' });
    }
  }

  async function reativar(d: Dispositivo) {
    try {
      await api.post(`/api/dispositivos/${d.id}/reativar`);
      await carregar();
      dialog.toast('Dispositivo reativado', { tipo: 'sucesso' });
    } catch (e: any) {
      dialog.toast(e.message ?? 'Falha ao reativar', { tipo: 'erro' });
    }
  }

  if (!ehAdmin) {
    return (
      <div style={{ padding: 24, color: 'var(--text-muted)' }}>
        Apenas administradores podem gerenciar dispositivos autorizados.
      </div>
    );
  }

  // Ligado = estado BOM/protegido -> amarelo positivo (daltonismo-safe, cor de "tudo certo").
  // Desligado = lembrete de atencao (NAO erro: o sistema segue como sempre) -> laranja, nao alarme.
  // Vermelho fica reservado a erro de verdade.
  const bannerLigado = { background: '#fde68a', color: '#000' } as const;
  const bannerAviso = { background: '#f97316', color: '#000' } as const;

  return (
    <div>
      <div className="pagina-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h2 className="pagina-titulo">
          <ShieldCheck size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          Dispositivos autorizados
        </h2>
        <button
          type="button"
          className="btn-primario"
          onClick={abrirModal}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Laptop size={15} /> Autorizar este computador
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          <Loader2 size={18} className="animate-spin" style={{ display: 'inline' }} /> Carregando...
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16, maxWidth: 760 }}>
          {/* Status DESTE computador (o que abriu a tela) — confirmacao por-maquina */}
          {esteComputador?.autorizado ? (
            <div style={{ padding: '12px 14px', borderRadius: 8, background: '#fde68a', color: '#000', fontWeight: 700, fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShieldCheck size={18} style={{ flexShrink: 0 }} />
              Este computador está autorizado e validado{esteComputador.nome ? ` (${esteComputador.nome})` : ''} — o certificado está instalado e funcionando corretamente.
            </div>
          ) : (
            <div style={{ padding: '12px 14px', borderRadius: 8, background: 'var(--card-bg)', border: '1px dashed var(--border)', color: 'var(--text-muted)', fontSize: 12.5, lineHeight: 1.5, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <Laptop size={16} style={{ marginTop: 1, flexShrink: 0 }} />
              <span>Este computador ainda não tem o certificado instalado. Clique em <strong>"Autorizar este computador"</strong> para liberá-lo — depois instale o arquivo baixado.</span>
            </div>
          )}

          {/* Interruptor da trava */}
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 2px' }}>Exigir dispositivo autorizado</h3>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: 520 }}>
                  Quando ligado, só os computadores desta lista (com o certificado instalado) acessam o sistema.
                  Os logins de emergência do Ruan entram de qualquer máquina.
                </div>
              </div>
              <button
                type="button"
                onClick={() => void alternarFlag(!ativo)}
                disabled={salvandoFlag}
                aria-pressed={ativo}
                style={{
                  minWidth: 132,
                  padding: '9px 16px',
                  borderRadius: 6,
                  border: 0,
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: salvandoFlag ? 'default' : 'pointer',
                  background: ativo ? '#f97316' : 'var(--input-bg, #e5e7eb)',
                  color: ativo ? '#000' : 'var(--page-fg)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                {salvandoFlag ? <Loader2 size={14} className="animate-spin" /> : null}
                {ativo ? 'LIGADO' : 'Desligado'}
              </button>
            </div>

            <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 6, fontSize: 12.5, fontWeight: 600, lineHeight: 1.5, ...(ativo ? bannerLigado : bannerAviso) }}>
              <AlertTriangle size={15} style={{ display: 'inline', marginRight: 6, marginBottom: -2 }} />
              {ativo
                ? 'A trava está LIGADA: máquinas sem certificado autorizado NÃO conseguem entrar. Se precisar liberar um computador novo, autorize-o antes de usá-lo.'
                : 'A trava está desligada — qualquer máquina com login válido entra normalmente (como hoje). Só ligue depois de autorizar os computadores da loja.'}
            </div>
          </div>

          {/* Lista de dispositivos */}
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Laptop size={16} /> Computadores cadastrados
            </h3>
            {lista.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '8px 0' }}>
                Nenhum computador autorizado ainda. Use <b>Autorizar este computador</b> em cada máquina da loja.
              </div>
            ) : (
              <table className="tabela" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Apelido</th>
                    <th style={{ textAlign: 'left' }}>Último acesso</th>
                    <th style={{ textAlign: 'left' }}>Status</th>
                    <th style={{ width: 44 }} />
                  </tr>
                </thead>
                <tbody>
                  {lista.map((d) => (
                    <tr key={d.id}>
                      <td>{d.nome}</td>
                      <td style={{ color: 'var(--text-muted)' }}>{formatarData(d.ultimo_acesso)}</td>
                      <td><StatusBadge ativo={d.ativo} labelAtivo="Ativo" labelInativo="Revogado" /></td>
                      <td style={{ textAlign: 'right' }}>
                        <MenuAcoesLinha
                          acoes={[
                            { label: 'Revogar', icon: Ban, perigoso: true, oculto: !d.ativo, onClick: () => void revogar(d) },
                            { label: 'Reativar', icon: RotateCcw, oculto: d.ativo, onClick: () => void reativar(d) },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      <Modal
        aberto={modalAberto}
        onFechar={() => setModalAberto(false)}
        titulo={emitido ? 'Certificado gerado' : 'Autorizar este computador'}
        largura={560}
      >
        {!emitido ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Dê um apelido para reconhecer este computador na lista (ex: <b>Balcão 1</b>, <b>Notebook do escritório</b>).
              Ao confirmar, o navegador baixa o certificado (e um arquivo que faz o Chrome parar de perguntar qual certificado usar) e mostra a senha para instalar.
            </div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
              Apelido do computador
              <input
                value={apelido}
                onChange={(e) => setApelido(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void autorizar(); }}
                placeholder="Balcão 1"
                autoFocus
                style={{ width: '100%', marginTop: 4, padding: '8px 10px', border: '1px solid var(--input-border)', borderRadius: 4, fontSize: 14, background: 'var(--input-bg, #fff)' }}
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button type="button" onClick={() => setModalAberto(false)} style={{ background: '#e5e7eb', color: '#1f2937', border: 0, padding: '8px 20px', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Cancelar</button>
              <button type="button" className="btn-primario" onClick={() => void autorizar()} disabled={emitindo} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {emitindo ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                Autorizar
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ padding: '10px 12px', borderRadius: 6, background: '#f97316', color: '#000', fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}>
              <Download size={15} style={{ display: 'inline', marginRight: 6, marginBottom: -2 }} />
              O certificado <b>{nomeArquivo(emitido.nome)}</b> foi baixado. Guarde a senha abaixo — ela aparece <b>uma única vez</b>.
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Senha de uso único</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                <code style={{ flex: 1, fontFamily: 'var(--font-mono, monospace)', fontSize: 16, fontWeight: 700, letterSpacing: 0.5, padding: '10px 12px', background: '#fde68a', color: '#000', borderRadius: 6, userSelect: 'all', overflowX: 'auto' }}>
                  {emitido.senha}
                </code>
                <button
                  type="button"
                  onClick={() => void copiarSenha()}
                  title="Copiar senha"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 14px', borderRadius: 6, border: 0, fontWeight: 600, fontSize: 13, cursor: 'pointer', background: copiado ? '#16a34a' : 'var(--primary, #2563eb)', color: '#fff' }}
                >
                  {copiado ? <Check size={15} /> : <Copy size={15} />}
                  {copiado ? 'Copiado' : 'Copiar'}
                </button>
              </div>
            </div>

            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Passo 1 — instalar o certificado (≈30 segundos)</div>
              <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.7 }}>
                <li>Dê <b>dois cliques</b> no arquivo <b>{nomeArquivo(emitido.nome)}</b>.</li>
                <li>Abre o <b>Assistente de Importação de Certificados do Windows</b>.</li>
                <li>Clique em <b>Avançar</b>, <b>Avançar</b>.</li>
                <li>Cole a <b>senha acima</b> quando pedir.</li>
                <li><b>Concluir</b>. Pronto — este computador está liberado.</li>
              </ol>
            </div>

            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Passo 2 — parar de perguntar o certificado (uma vez só)</div>
              <button
                type="button"
                onClick={() => baixarLiberacaoNavegador(emitido.ca_issuer_cn)}
                className="btn-primario"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 10 }}
              >
                <Download size={15} /> Baixar liberação do navegador
              </button>
              <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.7 }}>
                <li>Clique no botão acima para baixar <b>fiodecobre-liberar-navegador.reg</b>.</li>
                <li>Dê <b>dois cliques</b> no arquivo baixado.</li>
                <li>Confirme com <b>Sim</b> e depois <b>Sim</b> (aviso azul do Windows).</li>
                <li>Reinicie o navegador por completo: no Chrome digite <b>chrome://restart</b> (no Edge, <b>edge://restart</b>) na barra de endereço e Enter.</li>
              </ol>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}>
                Serve para Chrome e Edge. Sem esse passo o sistema funciona igual — o navegador só volta a perguntar <i>qual</i> certificado usar a cada acesso.
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => baixarPfx(emitido.pfx_base64, emitido.nome)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#e5e7eb', color: '#1f2937', border: 0, padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                <Download size={14} /> Baixar certificado de novo
              </button>
              <button type="button" className="btn-primario" onClick={() => setModalAberto(false)}>Concluir</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
